'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { Calendar, Users, Clock, CreditCard, ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';


type BookingType = 'full-day' | 'hourly';

type BookingFormValues = {
  date: string;
  bookingType: BookingType;
  startHour: string;
  duration: string;
  guests: number;
  name: string;
  phone: string;
  email: string;
  specialRequests: string;
};

const hourOptions = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'];
const durationOptions = ['2', '3', '4', '5', '6', '8'];

// Backend integration point: replace with POST /api/bookings + Stripe payment intent
export default function BookingWidget() {
  const [bookingType, setBookingType] = useState<BookingType>('full-day');
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<BookingFormValues>({
    defaultValues: { bookingType: 'full-day', guests: 100 },
  });

  const watchedGuests = watch('guests', 100);
  const watchedDuration = watch('duration', '4');
  const watchedDate = watch('date');

  const priceFullDay = 95000;
  const priceHourly = 15000;
  const platformFee = bookingType === 'full-day' ? Math.round(priceFullDay * 0.05) : Math.round(priceHourly * Number(watchedDuration || 4) * 0.05);
  const subtotal = bookingType === 'full-day' ? priceFullDay : priceHourly * Number(watchedDuration || 4);
  const advance = Math.round((subtotal + platformFee) * 0.30);
  const remaining = subtotal + platformFee - advance;

  const onSubmit = async (data: BookingFormValues) => {
    setLoading(true);
    // Navigate to booking confirmation page
    await new Promise((r) => setTimeout(r, 600));
    setLoading(false);
    router.push('/booking-confirmation');
  };

  return (
    <div className="sticky top-20">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-booking overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 p-5">
          <div className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">Book This Venue</div>
          <div className="text-white text-2xl font-extrabold font-tabular">
            {bookingType === 'full-day' ? '₹95,000'
              : `₹${(priceHourly * Number(watchedDuration || 4)).toLocaleString('en-IN', { useGrouping: true })}`}
          </div>
          <div className="text-red-200 text-xs mt-0.5">
            {bookingType === 'full-day' ? 'Full day · 8 AM – 12 AM' : `${watchedDuration || 4} hours`}
          </div>
        </div>

        {/* Booking Type Toggle */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex rounded-xl border-2 border-gray-100 overflow-hidden">
            <button
              onClick={() => setBookingType('full-day')}
              className={`flex-1 py-2.5 text-sm font-bold transition-all ${
                bookingType === 'full-day' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Full Day
            </button>
            <button
              onClick={() => setBookingType('hourly')}
              className={`flex-1 py-2.5 text-sm font-bold transition-all ${
                bookingType === 'hourly' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Hourly
            </button>
          </div>
        </div>

        {/* Step Indicators */}
        <div className="px-4 pt-4 pb-2 flex items-center gap-2">
          <div className={`flex items-center gap-1.5 text-xs font-bold ${step === 1 ? 'text-red-600' : 'text-green-600'}`}>
            {step === 2 ? <CheckCircle size={14} /> : <span className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center text-xs">1</span>}
            Event Details
          </div>
          <div className="flex-1 h-px bg-gray-100" />
          <div className={`flex items-center gap-1.5 text-xs font-bold ${step === 2 ? 'text-red-600' : 'text-gray-400'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${step === 2 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-400'}`}>2</span>
            Your Details
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 flex flex-col gap-4">
          {step === 1 && (
            <>
              {/* Date */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Event Date</label>
                <div className="relative">
                  <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    {...register('date', { required: 'Please select a date' })}
                    className="w-full pl-9 pr-3 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-900"
                  />
                </div>
                {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date.message}</p>}
              </div>

              {/* Hourly options */}
              {bookingType === 'hourly' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Start Time</label>
                    <div className="relative">
                      <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <select
                        {...register('startHour', { required: bookingType === 'hourly' })}
                        className="w-full pl-8 pr-6 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-700 appearance-none bg-white cursor-pointer"
                      >
                        <option value="">Select</option>
                        {hourOptions.map((h) => (
                          <option key={`hour-${h}`} value={h}>{h}</option>
                        ))}
                      </select>
                      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Duration (hrs)</label>
                    <div className="relative">
                      <select
                        {...register('duration')}
                        className="w-full px-3 pr-6 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-700 appearance-none bg-white cursor-pointer"
                      >
                        {durationOptions.map((d) => (
                          <option key={`dur-${d}`} value={d}>{d} hrs</option>
                        ))}
                      </select>
                      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              )}

              {/* Guests */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Number of Guests</label>
                <div className="relative">
                  <Users size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    min={1}
                    max={800}
                    {...register('guests', {
                      required: 'Guest count required',
                      min: { value: 1, message: 'Minimum 1 guest' },
                      max: { value: 800, message: 'Maximum capacity is 800 guests' },
                    })}
                    className="w-full pl-9 pr-3 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-900"
                  />
                </div>
                {errors.guests && <p className="text-red-500 text-xs mt-1">{errors.guests.message}</p>}
                {Number(watchedGuests) > 800 && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-amber-600 text-xs font-medium">
                    <AlertCircle size={12} />
                    Exceeds venue capacity of 800
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="bg-gray-50 rounded-xl p-4 text-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">
                    {bookingType === 'full-day' ?'Full day rate'
                      : `₹15,000 × ${watchedDuration || 4} hrs`}
                  </span>
                  <span className="font-semibold font-tabular">₹{subtotal.toLocaleString('en-IN', { useGrouping: true })}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Platform fee (5%)</span>
                  <span className="font-semibold font-tabular">₹{platformFee.toLocaleString('en-IN', { useGrouping: true })}</span>
                </div>
                <hr className="border-gray-200 my-2" />
                <div className="flex justify-between font-bold text-gray-900">
                  <span>Total</span>
                  <span className="font-tabular">₹{(subtotal + platformFee).toLocaleString('en-IN', { useGrouping: true })}</span>
                </div>
                <div className="mt-3 p-2.5 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex justify-between text-xs">
                    <span className="text-red-700 font-semibold">Advance payable now (30%)</span>
                    <span className="text-red-700 font-bold font-tabular">₹{advance.toLocaleString('en-IN', { useGrouping: true })}</span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-gray-500">Remaining at venue</span>
                    <span className="text-gray-600 font-medium font-tabular">₹{remaining.toLocaleString('en-IN', { useGrouping: true })}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setStep(2)}
                className="w-full btn-primary justify-center py-3 text-sm"
              >
                Continue to Details
              </button>
            </>
          )}

          {step === 2 && (
            <>
              {/* Contact Details */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name</label>
                <input
                  type="text"
                  placeholder="Priya Mehta"
                  {...register('name', { required: 'Name is required' })}
                  className="w-full px-3 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-900 placeholder-gray-300"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  placeholder="+91 98765 43210"
                  {...register('phone', {
                    required: 'Phone number required',
                    pattern: { value: /^[+\d\s]{10,14}$/, message: 'Enter a valid phone number' },
                  })}
                  className="w-full px-3 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-900 placeholder-gray-300"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  placeholder="priya@example.com"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                  className="w-full px-3 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-900 placeholder-gray-300"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Special Requests <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  rows={3}
                  placeholder="E.g. Halal catering, stage setup, specific décor theme..."
                  {...register('specialRequests')}
                  className="w-full px-3 py-2.5 text-sm border-2 border-gray-100 hover:border-red-200 focus:border-red-400 rounded-xl outline-none transition-colors font-medium text-gray-900 placeholder-gray-300 resize-none"
                />
              </div>

              {/* Summary */}
              <div className="bg-red-50 rounded-xl p-3.5 border border-red-100 text-xs">
                <div className="font-bold text-red-800 mb-2 flex items-center gap-1.5">
                  <CreditCard size={13} />
                  Payment Summary
                </div>
                <div className="flex justify-between text-red-700 font-semibold">
                  <span>Advance due now (30%)</span>
                  <span className="font-tabular">₹{advance.toLocaleString('en-IN', { useGrouping: true })}</span>
                </div>
                <p className="text-red-500 mt-1.5 leading-snug">
                  Secure advance payment via UPI, card, or net banking. Booking confirmed instantly.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-bold text-sm rounded-xl transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary justify-center py-3 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ minWidth: 0 }}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <>
                      <CreditCard size={15} />
                      Pay ₹{advance.toLocaleString('en-IN', { useGrouping: true })}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>

        {/* Trust Signals */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-500" /> Instant confirmation</span>
            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-500" /> Secure payment</span>
          </div>
        </div>
      </div>
    </div>
  );
}